import CrudSection from "../components/CrudSection";
import FeatureDashboard from "../../../components/base/FeatureDashboard";
import { StatusTextBadge } from "../../../components/base/StatusTextBadge";
import type { CrudColumn, CrudField } from "../types";

// ===========================================================================
// Marketing — campañas, formularios y landing pages (mock data, demo).
// ===========================================================================

export function MarketingDashboard() {
  return (
    <FeatureDashboard noticeKey="crm.mockNotice"
      title="Marketing"
      subtitle="Campañas, formularios y captación de leads"
      icon="ri-megaphone-line"
      stats={[
        { label: "Campañas activas", value: "4", icon: "ri-rocket-line" },
        { label: "Envíos del mes", value: "12,480", icon: "ri-send-plane-line" },
        { label: "Formularios", value: "3", icon: "ri-file-list-3-line" },
        { label: "Conversión landing", value: "6.2%", icon: "ri-cursor-line", change: "+0.8%", positive: true },
      ]}
      chart={{
        title: "Leads generados por campaña",
        data: [
          { name: "Diagnóstico", value: 64 }, { name: "Webinar", value: 28 },
          { name: "Ebook", value: 19 }, { name: "Remarketing", value: 12 },
        ],
      }}
    />
  );
}

// --- Campañas --------------------------------------------------------------
interface Campana extends Record<string, unknown> {
  id: string; nombre: string; tipo: string; estado: string; leads: string;
}
const campanaColumns: CrudColumn<Campana>[] = [
  { key: "nombre", label: "Campaña" },
  { key: "tipo", label: "Tipo" },
  { key: "leads", label: "Leads" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const campanaFields: CrudField[] = [
  { key: "nombre", label: "Nombre", required: true, placeholder: "Diagnóstico gratis" },
  { key: "tipo", label: "Tipo", type: "select", required: true, options: [
    { value: "Email", label: "Email" }, { value: "WhatsApp", label: "WhatsApp" },
    { value: "Social", label: "Social" }, { value: "Landing", label: "Landing" },
  ] },
  { key: "leads", label: "Leads", type: "number", placeholder: "0" },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Activa", options: [
    { value: "Borrador", label: "Borrador" }, { value: "Activa", label: "Activa" },
    { value: "Pausada", label: "Pausada" }, { value: "Completada", label: "Completada" },
  ] },
];
const campanaSeed: Campana[] = [
  { id: "cp1", nombre: "Diagnóstico gratis", tipo: "Landing", leads: "64", estado: "Activa" },
  { id: "cp2", nombre: "Webinar automatización", tipo: "Email", leads: "28", estado: "Activa" },
  { id: "cp3", nombre: "Ebook integraciones", tipo: "Social", leads: "19", estado: "Pausada" },
];
export const CampanasSection = () => (
  <CrudSection<Campana> title="Campañas" subtitle="Iniciativas de marketing"
    icon="ri-rocket-line" columns={campanaColumns} fields={campanaFields} seed={campanaSeed} searchKey="nombre" />
);

// --- Formularios -----------------------------------------------------------
interface Formulario extends Record<string, unknown> {
  id: string; nombre: string; campos: string; envios: string; estado: string;
}
const formularioColumns: CrudColumn<Formulario>[] = [
  { key: "nombre", label: "Formulario" },
  { key: "campos", label: "Campos" },
  { key: "envios", label: "Envíos" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const formularioFields: CrudField[] = [
  { key: "nombre", label: "Nombre", required: true, placeholder: "Contacto landing" },
  { key: "campos", label: "Campos", type: "number", placeholder: "8" },
  { key: "envios", label: "Envíos", type: "number", placeholder: "0" },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Publicado", options: [
    { value: "Borrador", label: "Borrador" }, { value: "Publicado", label: "Publicado" },
  ] },
];
const formularioSeed: Formulario[] = [
  { id: "fm1", nombre: "Contacto landing (Diagnóstico)", campos: "9", envios: "128", estado: "Publicado" },
  { id: "fm2", nombre: "Newsletter", campos: "2", envios: "340", estado: "Publicado" },
];
export const FormulariosSection = () => (
  <CrudSection<Formulario> title="Formularios" subtitle="Captura de leads — el formulario de la landing alimenta el CRM"
    icon="ri-file-list-3-line" columns={formularioColumns} fields={formularioFields} seed={formularioSeed} searchKey="nombre" />
);

// --- Landing pages ---------------------------------------------------------
interface Landing extends Record<string, unknown> {
  id: string; nombre: string; url: string; visitas: string; conversion: string; estado: string;
}
const landingColumns: CrudColumn<Landing>[] = [
  { key: "nombre", label: "Landing" },
  { key: "url", label: "URL" },
  { key: "visitas", label: "Visitas" },
  { key: "conversion", label: "Conversión" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const landingFields: CrudField[] = [
  { key: "nombre", label: "Nombre", required: true, placeholder: "Diagnóstico gratis" },
  { key: "url", label: "URL", placeholder: "/diagnostico" },
  { key: "visitas", label: "Visitas", type: "number", placeholder: "0" },
  { key: "conversion", label: "Conversión", placeholder: "6%" },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Publicada", options: [
    { value: "Borrador", label: "Borrador" }, { value: "Publicada", label: "Publicada" },
  ] },
];
const landingSeed: Landing[] = [
  { id: "lp1", nombre: "Diagnóstico gratis", url: "/", visitas: "2,140", conversion: "6.2%", estado: "Publicada" },
];
export const LandingPagesSection = () => (
  <CrudSection<Landing> title="Landing pages" subtitle="Páginas de captación"
    icon="ri-pages-line" columns={landingColumns} fields={landingFields} seed={landingSeed} searchKey="nombre" />
);
